import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import {
  Cube, Truck, ChartBar, Rocket, ShieldCheck, Lightning, ClockCounterClockwise, Package,
  Storefront, Buildings, Factory, Boat, ChatCircle, Envelope, MapPin, Phone,
  ArrowRight, Check, Play, Sparkle, Sun, Moon,
} from "@phosphor-icons/react";

const NAV_ITEMS = [
  { key: "hero", tKey: "landing.nav.home" },
  { key: "features", tKey: "landing.nav.features" },
  { key: "how", tKey: "landing.nav.how" },
  { key: "sectors", tKey: "landing.nav.sectors" },
  { key: "pricing", tKey: "landing.nav.pricing" },
  { key: "demo", tKey: "landing.nav.demo" },
  { key: "about", tKey: "landing.nav.about" },
  { key: "contact", tKey: "landing.nav.contact" },
];

export default function Landing() {
  const { t } = useI18n();
  const nav = useNavigate();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [publicPricing, setPublicPricing] = useState({ pro: 599, business: 999 });
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("h3poe_landing_theme") || "dark"; } catch (_) { return "dark"; }
  });
  const isLight = theme === "light";

  useEffect(() => {
    try { localStorage.setItem("h3poe_landing_theme", theme); } catch (err) { console.warn("theme persist failed:", err); }
  }, [theme]);

  useEffect(() => {
    api.get("/public/pricing").then((r) => {
      setPublicPricing({ pro: Number(r.data?.pro || 599), business: Number(r.data?.business || 999) });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Fire-and-forget visitor tracking (once per page load).
  useEffect(() => {
    const key = "h3poe_landing_ping";
    try {
      const last = sessionStorage.getItem(key);
      if (!last) {
        api.post("/analytics/track", { event: "landing_visit" }).catch(() => {});
        sessionStorage.setItem(key, String(Date.now()));
      }
    } catch (err) { console.warn("landing_visit tracking failed:", err); }
  }, []);

  const track = (event, label = "") => {
    api.post("/analytics/track", { event, label }).catch(() => {});
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className={`min-h-screen ${isLight ? "bg-white text-slate-900" : "bg-white text-slate-900"}`} data-testid="landing-page" data-theme={theme}>
      {/* ============ NAVBAR ============ */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? (isLight ? "bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm" : "bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm") : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <div className="flex items-center cursor-pointer" onClick={() => scrollTo("hero")}><img src="/brand/palletnext-logo.png" alt="PalletNext H3POE" className="h-10 sm:h-12 w-auto object-contain" /></div>
          <nav className="hidden lg:flex items-center gap-6">
            {NAV_ITEMS.map((n) => (
              <button key={n.key} onClick={() => scrollTo(n.key)}
                data-testid={`landing-nav-${n.key}`}
                className="text-sm font-medium text-slate-700 hover:text-[#2563EB] transition-colors">
                {t(n.tKey)}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setTheme(isLight ? "dark" : "light")}
              data-testid="landing-theme-toggle"
              aria-label={t("landing.theme")}
              className="w-9 h-9 flex items-center justify-center rounded-sm border border-slate-300 hover:border-slate-400 hover:bg-slate-50 text-slate-600 transition-colors">
              {isLight ? <Moon size={16} weight="duotone" /> : <Sun size={16} weight="duotone" />}
            </button>
            <button
              onClick={() => { track("cta_click", "nav_login"); nav("/login"); }}
              data-testid="landing-login-btn"
              className="px-3 sm:px-4 py-2 text-sm font-semibold text-slate-700 hover:text-[#2563EB] transition-colors">
              {t("landing.login")}
            </button>
            <button
              onClick={() => { track("cta_click", "nav_register"); nav("/login?tab=register"); }}
              data-testid="landing-register-btn"
              className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-sm">
              {t("landing.free_try")} <ArrowRight size={14} weight="bold" className="hidden sm:inline" />
            </button>
          </div>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section id="hero" className={`relative pt-28 sm:pt-32 pb-16 sm:pb-24 overflow-hidden ${isLight ? "bg-slate-100 text-slate-900" : "bg-[#0F172A] text-white"}`}>
        <div className={`absolute inset-0 ${isLight ? "opacity-30" : "opacity-20"}`}
          style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #2563EB 0%, transparent 45%), radial-gradient(circle at 80% 60%, #16A34A 0%, transparent 40%)" }} />
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: `linear-gradient(${isLight ? "#000" : "#fff"} 1px, transparent 1px), linear-gradient(90deg, ${isLight ? "#000" : "#fff"} 1px, transparent 1px)`, backgroundSize: "40px 40px" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <div className={`inline-flex items-center gap-2 border rounded-full px-3 py-1 text-[10px] sm:text-[11px] uppercase tracking-widest mb-4 sm:mb-6 ${isLight ? "border-slate-300 text-slate-600" : "border-white/20 text-slate-300"}`}>
              <Sparkle size={12} weight="fill" className="text-amber-400" /> {t("landing.hero.badge")}
            </div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t("landing.hero.title1")} <span className={isLight ? "text-[#F59E0B]" : "text-[#FACC15]"}>{t("landing.hero.title2")}</span>
            </h1>
            <p className={`mt-4 sm:mt-6 text-base sm:text-lg max-w-xl ${isLight ? "text-slate-600" : "text-slate-300"}`}>
              {t("landing.hero.desc")}
            </p>
            <div className="mt-6 sm:mt-8 flex flex-wrap items-center gap-3">
              <button onClick={() => nav("/login?tab=register")}
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold rounded-sm text-sm">
                {t("landing.hero.try")} <ArrowRight size={16} weight="bold" />
              </button>
              <button onClick={() => scrollTo("demo")}
                data-testid="hero-cta-demo"
                className={`inline-flex items-center gap-2 px-5 sm:px-6 py-3 border font-semibold rounded-sm text-sm ${isLight ? "border-slate-400 hover:border-slate-600 text-slate-800" : "border-white/30 hover:border-white/60 text-white"}`}>
                <Play size={14} weight="fill" /> {t("landing.hero.demo")}
              </button>
            </div>
            <div className="mt-8 sm:mt-10 grid grid-cols-3 gap-3 sm:gap-4 font-mono text-xs max-w-md">
              <Stat n="4" l="Strateji" isLight={isLight} />
              <Stat n="6" l="Rotasyon" isLight={isLight} />
              <Stat n="3D" l="Yerleşim" isLight={isLight} />
            </div>
          </div>
          <div className="relative">
            <div className="relative bg-slate-800 rounded-lg p-6 border border-slate-700 shadow-2xl">
              <div className="flex items-center gap-1.5 mb-4">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                <div className="ml-3 font-mono text-[10px] uppercase text-slate-500 tracking-widest">plan-P0247 · optimizasyon</div>
              </div>
              <div className="grid grid-cols-4 gap-1.5 h-64">
                {Array.from({ length: 32 }).map((_, i) => {
                  const colors = ["#38BDF8", "#22C55E", "#F59E0B", "#EF4444"];
                  const c = colors[i % 4];
                  const skip = [3, 7, 11, 15, 19, 23, 27, 31].includes(i);
                  return (
                    <div key={i}
                      className="rounded-sm border border-white/10 transition-transform hover:scale-105"
                      style={{ background: skip ? "transparent" : `linear-gradient(135deg, ${c} 0%, ${c}88 100%)`, border: skip ? "1px dashed #334155" : undefined }} />
                  );
                })}
              </div>
              <div className="mt-4 flex justify-between font-mono text-[10px] text-slate-400 uppercase tracking-wider">
                <span>32 kutu</span><span>%92 doluluk</span><span>145kg</span>
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 bg-emerald-500 text-white px-3 py-2 text-xs font-bold rounded-sm shadow-lg animate-pulse">
              {t("landing.hero.fit")}
            </div>
          </div>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <Section id="features" eyebrow={t("landing.features.eyebrow")} title={t("landing.features.title")} isLight={isLight}>
        <div className="grid md:grid-cols-3 gap-5">
          <Feature icon={Cube} title="H3POE 3D Optimizasyon"
            text={t("landing.features.h3poe.text")} />
          <Feature icon={Lightning} title={t("landing.features.recipe.title")}
            text={t("landing.features.recipe.text")} />
          <Feature icon={Package} title="PDF & Excel Export"
            text={t("landing.features.docs.text2")} />
          <Feature icon={ChartBar} title="Test Merkezi"
            text={t("landing.features.test.text2")} />
          <Feature icon={ShieldCheck} title={t("landing.features.manual.title")}
            text={t("landing.features.manual.text")} />
          <Feature icon={ClockCounterClockwise} title={t("landing.features.fx.title")}
            text={t("landing.features.fx.text")} />
        </div>
      </Section>

      {/* ============ HOW IT WORKS ============ */}
      <Section id="how" eyebrow={t("landing.how.eyebrow")} title={t("landing.how.title")} dark isLight={isLight}>
        <div className="grid md:grid-cols-4 gap-4">
          <Step n="01" title={t("landing.how.1.title")} text={t("landing.how.1.text")} isLight={isLight} />
          <Step n="02" title={t("landing.how.2.title")} text={t("landing.how.2.text")} isLight={isLight} />
          <Step n="03" title={t("landing.how.3.title")} text={t("landing.how.3.text")} isLight={isLight} />
          <Step n="04" title={t("landing.how.4.title")} text={t("landing.how.4.text")} isLight={isLight} />
        </div>
      </Section>

      {/* ============ SECTORS ============ */}
      <Section id="sectors" eyebrow={t("landing.sectors.eyebrow")} title={t("landing.sectors.title")} isLight={isLight}>
        <div className="grid md:grid-cols-4 gap-4">
          <SectorCard icon={Storefront} title={t("landing.sectors.electronics.title2")}
            text={t("landing.sectors.electronics.text2")} />
          <SectorCard icon={Buildings} title="Perakende Zincirleri"
            text={t("landing.sectors.retail.text2")} />
          <SectorCard icon={Factory} title={t("landing.sectors.factory.title2")}
            text={t("landing.sectors.factory.text2")} />
          <SectorCard icon={Boat} title="Lojistik & Nakliye"
            text={t("landing.sectors.export.text2")} />
        </div>
      </Section>

      {/* ============ PRICING ============ */}
      <Section id="pricing" eyebrow={t("landing.pricing.eyebrow")} title={t("landing.pricing.title")} isLight={isLight}>
        <div className="grid md:grid-cols-3 gap-4">
          <PriceCard tier={t("landing.pricing.free")} price={t("landing.pricing.free_price")} period={t("landing.pricing.free_period")}
            features={[t("landing.pricing.free_f1"), t("landing.pricing.free_f2"), t("landing.pricing.free_f3"), t("landing.pricing.free_f4")]}
            cta={t("landing.pricing.free_cta")} onCta={() => { track("pricing_click", "free"); nav("/login?tab=register"); }} testid="price-free" />
          <PriceCard tier="Pro" price={`₺${Number(publicPricing.pro || 599).toLocaleString("tr-TR")}`} period="/ ay" highlighted
            features={["200 Plan/Ay", "500 ürün", "H3POE Multi-Core", "Reçete & Learning", "Packing List & Invoice", "Şablon tasarımcısı"]}
            cta="Pro'yu Başlat" onCta={() => { track("pricing_click", "pro"); nav("/login?tab=register"); }} testid="price-pro" />
          <PriceCard tier="Business" price={`₺${Number(publicPricing.business || 999).toLocaleString("tr-TR")}`} period="/ ay"
            features={["1000 Plan/Ay", "2000 ürün", "Warehouse Execution", "API & ERP", "Audit Log", "Öncelikli operasyon araçları", "H3POE Multi-Core", "Reçete & Learning", "Packing List & Invoice", "Şablon tasarımcısı"]}
            cta="Business'ı Başlat" onCta={() => { track("pricing_click", "business"); nav("/login?tab=register"); }} testid="price-business" />
        </div>
      </Section>

      {/* ============ DEMO ============ */}
      <Section id="demo" eyebrow={t("landing.demo.eyebrow")} title={t("landing.demo.title")} dark isLight={isLight}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="aspect-video bg-slate-900 border border-slate-700 rounded-lg relative overflow-hidden shadow-2xl">
            <video
              data-testid="landing-demo-video"
              className="w-full h-full object-contain bg-slate-950"
              src="/demo/palletnext-demo.mp4"
              controls
              playsInline
              preload="metadata"
            >
              Tarayıcınız video oynatmayı desteklemiyor.
            </video>
          </div>
          <button onClick={() => nav("/login")}
            data-testid="demo-login-btn"
            className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold rounded-sm text-sm">
            {t("landing.demo.open")} <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </Section>

      {/* ============ ABOUT ============ */}
      <Section id="about" eyebrow={t("landing.about.eyebrow")} title={t("landing.about.title")} isLight={isLight}>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>{t("landing.about.p1")}</p>
            <p>{t("landing.about.p2b")}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <MetricCard n="PDF/XLSX" l="Belge Çıktısı" />
            <MetricCard n="QR" l="Palet Doğrulama" />
            <MetricCard n="3D" l="Dijital Yerleşim" />
            <MetricCard n="API" l="ERP Entegrasyonu" />
          </div>
        </div>
      </Section>

      {/* ============ CONTACT ============ */}
      <Section id="contact" eyebrow={t("landing.contact.eyebrow")} title={t("landing.contact.title")} dark isLight={isLight}>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div className="space-y-4">
            <ContactLine icon={Envelope} label="E-posta" value="İletişim formu üzerinden ulaşın" />
            <ContactLine icon={Phone} label="Telefon" value="Talep üzerine paylaşılır" />
            <ContactLine icon={MapPin} label={t("landing.contact.address")} value={t("landing.contact.address_value")} />
            <ContactLine icon={ChatCircle} label={t("landing.contact.support")} value={t("landing.contact.support_hours")} />
          </div>
          <ContactForm />
        </div>
      </Section>

      {/* ============ FOOTER ============ */}
      <footer className="bg-slate-950 text-slate-400 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between gap-4 text-sm">
          <div>
            <div className="flex items-center"><img src="/brand/palletnext-logo.png" alt="PalletNext H3POE" className="h-9 w-auto object-contain" /></div>
            <div className="mt-2 text-xs">© 2026 H3POE. {t("landing.footer.rights")}</div>
          </div>
          <div className="flex gap-6 text-xs">
            <button onClick={() => scrollTo("features")} className="hover:text-white">{t("landing.nav.features")}</button>
            <button onClick={() => scrollTo("pricing")} className="hover:text-white">{t("landing.nav.pricing")}</button>
            <button onClick={() => scrollTo("contact")} className="hover:text-white">{t("landing.nav.contact")}</button>
            <button onClick={() => nav("/login")} className="hover:text-white">{t("landing.login")}</button>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ================ Building blocks ================
function Stat({ n, l, isLight }) {
  return (
    <div className={`border p-3 rounded-sm ${isLight ? "border-slate-300 bg-white" : "border-white/15"}`}>
      <div className={`uppercase text-[10px] tracking-widest ${isLight ? "text-slate-500" : "text-slate-400"}`}>{l}</div>
      <div className={`text-lg font-bold mt-0.5 ${isLight ? "text-slate-900" : "text-white"}`}>{n}</div>
    </div>
  );
}

function Section({ id, eyebrow, title, children, dark, isLight }) {
  // Only Hero+"light-native" sections swap on light theme. Sections with dark=true
  // contain white children (Step, ContactLine, ContactForm) and remain dark for safety.
  const useDark = !!dark;
  return (
    <section id={id} className={`py-14 sm:py-20 ${useDark ? "bg-[#0F172A] text-white" : (isLight ? "bg-slate-50 text-slate-900" : "bg-white text-slate-900")}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-12">
          <div className={`text-[10px] sm:text-[11px] uppercase tracking-widest ${useDark ? "text-blue-400" : "text-[#2563EB]"} font-semibold`}>{eyebrow}</div>
          <h2 className="mt-2 font-display font-extrabold text-2xl sm:text-3xl md:text-4xl tracking-tight">{title}</h2>
        </div>
        {children}
      </div>
    </section>
  );
}

function Feature({ icon: Icon, title, text }) {
  return (
    <div className="border border-slate-200 rounded-sm p-5 hover:border-[#2563EB] hover:shadow-lg transition-all">
      <div className="w-10 h-10 bg-blue-50 text-[#2563EB] flex items-center justify-center rounded-sm mb-3">
        <Icon size={20} weight="duotone" />
      </div>
      <h3 className="font-display font-bold text-lg">{title}</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
    </div>
  );
}

function Step({ n, title, text }) {
  return (
    <div className="border border-white/15 rounded-sm p-5 relative">
      <div className="text-[#FACC15] font-mono text-2xl font-bold">{n}</div>
      <div className="mt-2 font-display font-bold text-lg text-white">{title}</div>
      <div className="mt-1 text-sm text-slate-300">{text}</div>
    </div>
  );
}

function SectorCard({ icon: Icon, title, text }) {
  return (
    <div className="border border-slate-200 rounded-sm p-5 hover:bg-slate-50 transition-colors">
      <Icon size={26} weight="duotone" className="text-[#2563EB]" />
      <div className="mt-3 font-display font-bold text-base">{title}</div>
      <div className="mt-1.5 text-sm text-slate-600">{text}</div>
    </div>
  );
}

function PriceCard({ tier, price, period, features, cta, onCta, highlighted, testid }) {
  const { t } = useI18n();
  return (
    <div data-testid={testid}
      className={`p-6 rounded-sm border ${highlighted ? "border-[#2563EB] bg-[#0F172A] text-white ring-4 ring-blue-100" : "border-slate-200 bg-white"} relative`}>
      {highlighted && (
        <div className="absolute -top-3 left-6 bg-[#FACC15] text-slate-900 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm">{t("landing.pricing.popular")}</div>
      )}
      <div className={`text-xs uppercase tracking-widest ${highlighted ? "text-blue-300" : "text-[#2563EB]"} font-semibold`}>{tier}</div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="font-display font-extrabold text-4xl">{price}</span>
        <span className={`text-sm ${highlighted ? "text-slate-400" : "text-slate-500"}`}>{period}</span>
      </div>
      <ul className="mt-5 space-y-2">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check size={16} weight="bold" className={highlighted ? "text-emerald-400 mt-0.5" : "text-emerald-600 mt-0.5"} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button onClick={onCta}
        className={`mt-6 w-full py-2.5 text-sm font-semibold rounded-sm ${highlighted ? "bg-[#2563EB] hover:bg-[#1D4ED8] text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-900"}`}>
        {cta}
      </button>
    </div>
  );
}

function MetricCard({ n, l }) {
  return (
    <div className="border border-slate-200 rounded-sm p-4">
      <div className="font-display font-extrabold text-3xl text-[#2563EB]">{n}</div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{l}</div>
    </div>
  );
}

function ContactLine({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 bg-white/10 border border-white/20 flex items-center justify-center rounded-sm">
        <Icon size={16} weight="duotone" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
        <div className="mt-0.5 text-sm text-white">{value}</div>
      </div>
    </div>
  );
}

function ContactForm() {
  const { t } = useI18n();
  const [f, setF] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  return (
    <form onSubmit={(e) => { e.preventDefault(); setSent(true); }}
      className="bg-white/5 border border-white/10 rounded-sm p-5 space-y-3">
      <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
        placeholder={t("landing.form.name")} required
        className="w-full bg-transparent border border-white/20 px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#2563EB]" />
      <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
        type="email" placeholder="Email" required
        className="w-full bg-transparent border border-white/20 px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#2563EB]" />
      <textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })}
        placeholder={t("landing.form.message")} rows={4} required
        className="w-full bg-transparent border border-white/20 px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#2563EB]" />
      <button type="submit" data-testid="contact-submit"
        className="w-full py-2.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-semibold rounded-sm">
        {sent ? t("landing.form.sent") : t("landing.form.send")}
      </button>
    </form>
  );
}
